import { Stack } from '@mui/joy';
import { ColDef } from 'ag-grid-community';
import { CustomCellRendererProps } from 'ag-grid-react';
import { t } from 'i18next';

import { AutocompleteFilter } from 'core/components/AgGridTable';
import { DEFAULT_PLACEHOLDER, EMPTY } from 'core/constants';
import { CellRendererForAgGrid, LinkCell, StatusCell, TextLine } from 'core/elements';
import { AgGridFilterOption, LinkTarget, SortDirection } from 'core/enums';
import { formatDate } from 'core/utils';
import { CorporateUserType } from 'corporate/enums';
import { corporateUserService } from 'corporate/services';
import { ICorporateUserAutocomplete } from 'corporate/types';
import { orderPartsPath } from 'orders/routes/routePaths';
import { PricingHelper } from 'pricing/helpers';
import { statusColorMap } from 'quotes/constants';
import {
  FilenetStatusCell,
  QuoteIdCell,
  QuoteStateCell,
  TradeAgreementOwnerCell,
  TradeAgreementStatusCell,
  VendorNameCell,
} from 'quotes/elements';
import {
  FilenetStatus,
  FilenetStatusLabel,
  QuoteEntityType,
  QuoteListType,
  QuoteState,
  QuoteStatusLabel,
  TradeAgreementStatus,
} from 'quotes/enums';
import { IBaseQuote, ICustomerQuote, IVendorQuote } from 'quotes/types';

import { VendorQuoteColId } from './enums';
import { getQuoteStatuses } from './utils';

// Column visibility gates, all computed by the caller. Each optional column follows the rule of
// the action it reports on, so a user who can never trigger the action never sees its column.
interface IColumnGates {
  // Alternate version on: the supplier (Vendor Name / AP number) column.
  showSupplierInfo?: boolean;
  // Alternate version on AND the SEND_TO_FILENET role — the Send to FileNet button's rule.
  showFilenetStatus?: boolean;
  // Company alternate-export capability (VendorQuoteHelper.isAlternateExportEnabled) only — the
  // Trade Agreement columns are read-only info; the SEND_AS_SPA role gates just the send actions.
  showTradeAgreementInfo?: boolean;
}

interface IOptions extends IColumnGates {
  quoteListType?: QuoteListType;
}

export const getColumnDef = (
  entityType = QuoteEntityType.QUOTE,
  { quoteListType = QuoteListType.GLOBAL, ...gates }: IOptions = {}
): ColDef[] => {
  const allColumns = getVendorQuoteListColumnDef(entityType, { quoteListType });

  switch (entityType) {
    case QuoteEntityType.ORDER:
      return getOrderColumnDef(allColumns, quoteListType);
    case QuoteEntityType.QUOTE:
    default:
      return getQuoteColumnDef(allColumns, quoteListType, gates);
  }
};

const getQuoteColumnDef = (
  allColumns: Record<string, ColDef>,
  quoteListType = QuoteListType.GLOBAL,
  {
    showSupplierInfo = false,
    showFilenetStatus = false,
    showTradeAgreementInfo = false,
  }: IColumnGates = {}
) => {
  const {
    statusColumn,
    quoteIdColumn,
    vendorCompanyColumn,
    vendorContactColumn,
    jobColumn,
    projectColumn,
    vendorProjectColumn,
    totalPriceColumn,
    userColumn,
    locationColumn,
    updatedAtColumn,
    createdAtColumn,
    archivedColumn,
    manufacturersColumn,
    vendorQuoteColumn,
    vendorNameColumn,
    filenetStatusColumn,
    validUntilColumn,
    tradeAgreementStatusColumn,
    tradeAgreementOwnerColumn,
  } = allColumns;

  // FileNet + supplier columns go on the real list variants only, never the COPY_PARTS_TO_QUOTE
  // picker.
  const filenetStatusColumns = showFilenetStatus ? [filenetStatusColumn] : [];
  const vendorNameColumns = showSupplierInfo ? [vendorNameColumn] : [];
  const tradeAgreementColumns = showTradeAgreementInfo
    ? [tradeAgreementStatusColumn, tradeAgreementOwnerColumn]
    : [];

  switch (quoteListType) {
    case QuoteListType.COPY_PARTS_TO_QUOTE:
      return [
        statusColumn,
        quoteIdColumn,
        jobColumn,
        projectColumn,
        totalPriceColumn,
        updatedAtColumn,
        createdAtColumn,
        userColumn,
        locationColumn,
        manufacturersColumn,
        vendorQuoteColumn,
        validUntilColumn,
        ...tradeAgreementColumns,
        archivedColumn,
      ];
    case QuoteListType.JOB:
      return [
        statusColumn,
        ...filenetStatusColumns,
        quoteIdColumn,
        vendorCompanyColumn,
        vendorContactColumn,
        ...vendorNameColumns,
        totalPriceColumn,
        updatedAtColumn,
        createdAtColumn,
        userColumn,
        locationColumn,
        manufacturersColumn,
        vendorQuoteColumn,
        validUntilColumn,
        archivedColumn,
      ];
    case QuoteListType.PROJECT:
      return [
        statusColumn,
        ...filenetStatusColumns,
        quoteIdColumn,
        vendorCompanyColumn,
        vendorContactColumn,
        ...vendorNameColumns,
        jobColumn,
        projectColumn,
        totalPriceColumn,
        updatedAtColumn,
        createdAtColumn,
        userColumn,
        locationColumn,
        manufacturersColumn,
        vendorQuoteColumn,
        validUntilColumn,
        ...tradeAgreementColumns,
        archivedColumn,
      ];
    case QuoteListType.GLOBAL:
    default:
      return [
        statusColumn,
        ...filenetStatusColumns,
        quoteIdColumn,
        vendorCompanyColumn,
        vendorContactColumn,
        ...vendorNameColumns,
        jobColumn,
        projectColumn,
        vendorProjectColumn,
        totalPriceColumn,
        updatedAtColumn,
        createdAtColumn,
        userColumn,
        locationColumn,
        manufacturersColumn,
        vendorQuoteColumn,
        validUntilColumn,
        ...tradeAgreementColumns,
        archivedColumn,
      ];
  }
};

const getOrderColumnDef = (
  allColumns: Record<string, ColDef>,
  quoteListType = QuoteListType.GLOBAL
) => {
  const {
    statusColumn,
    orderIdColumn,
    quoteIdColumn,
    vendorCompanyColumn,
    vendorContactColumn,
    jobColumn,
    projectColumn,
    totalPriceColumn,
    userColumn,
    locationColumn,
    updatedAtColumn,
    createdAtColumn,
    archivedColumn,
    customerNumberColumn,
    purchaseOrderColumn,
    externalOrderColumn,
    externalPendingOrderColumn,
  } = allColumns;

  switch (quoteListType) {
    case QuoteListType.COPY_PARTS_TO_QUOTE:
      return [
        statusColumn,
        orderIdColumn,
        jobColumn,
        projectColumn,
        quoteIdColumn,
        totalPriceColumn,
        updatedAtColumn,
        createdAtColumn,
        userColumn,
        locationColumn,
        customerNumberColumn,
        purchaseOrderColumn,
        externalOrderColumn,
        externalPendingOrderColumn,
        archivedColumn,
      ];
    case QuoteListType.JOB:
      return [
        statusColumn,
        orderIdColumn,
        vendorCompanyColumn,
        vendorContactColumn,
        quoteIdColumn,
        totalPriceColumn,
        updatedAtColumn,
        createdAtColumn,
        userColumn,
        locationColumn,
        customerNumberColumn,
        purchaseOrderColumn,
        externalOrderColumn,
        externalPendingOrderColumn,
        archivedColumn,
      ];
    case QuoteListType.PROJECT:
      return [
        statusColumn,
        orderIdColumn,
        vendorCompanyColumn,
        vendorContactColumn,
        jobColumn,
        quoteIdColumn,
        totalPriceColumn,
        updatedAtColumn,
        createdAtColumn,
        userColumn,
        locationColumn,
        customerNumberColumn,
        purchaseOrderColumn,
        externalOrderColumn,
        externalPendingOrderColumn,
        archivedColumn,
      ];
    case QuoteListType.GLOBAL:
    default:
      return [
        statusColumn,
        orderIdColumn,
        vendorCompanyColumn,
        vendorContactColumn,
        jobColumn,
        projectColumn,
        quoteIdColumn,
        totalPriceColumn,
        updatedAtColumn,
        createdAtColumn,
        userColumn,
        locationColumn,
        customerNumberColumn,
        purchaseOrderColumn,
        externalOrderColumn,
        externalPendingOrderColumn,
        archivedColumn,
      ];
  }
};

export const getVendorQuoteListColumnDef = (
  entityType = QuoteEntityType.QUOTE,
  { quoteListType = QuoteListType.GLOBAL }: IOptions = {}
) => {
  const isOrder = QuoteEntityType.ORDER === entityType;
  const isGlobal = quoteListType === QuoteListType.GLOBAL;

  return {
    statusColumn: {
      colId: VendorQuoteColId.STATUS,
      field: 'status',
      headerName: t('headerStatus'),
      maxWidth: 150,
      filter: 'agSetColumnFilter',
      filterParams: {
        valueFormatter: ({ value }) => QuoteStatusLabel[value],
        values: getQuoteStatuses(entityType, {
          excludeImported: QuoteListType.COPY_PARTS_TO_QUOTE === quoteListType,
        }),
        defaultToNothingSelected: true,
      },
      cellRenderer: StatusCell,
      cellRendererParams: {
        colorMap: statusColorMap,
        labelMap: Object.fromEntries(Object.entries(QuoteStatusLabel)),
      },
    },
    orderIdColumn: {
      colId: VendorQuoteColId.ID,
      headerName: t('headerOrderId'),
      field: 'id',
      filter: 'agNumberColumnFilter',
      cellRenderer: LinkCell,
      cellRendererParams: {
        getPath: (data: ICustomerQuote) => orderPartsPath(data.id.toString()),
        target: isGlobal ? LinkTarget.SELF : LinkTarget.BLANK,
      },
    },
    quoteIdColumn: {
      colId: isOrder ? VendorQuoteColId.PARENT_ID : VendorQuoteColId.ID,
      headerName: t('headerQuoteId'),
      field: isOrder ? 'parentId' : 'id',
      filter: 'agNumberColumnFilter',
      cellRenderer: QuoteIdCell,
      cellRendererParams: {
        target: isGlobal ? LinkTarget.SELF : LinkTarget.BLANK,
      },
    },
    vendorCompanyColumn: {
      colId: VendorQuoteColId.VENDOR_CONTACT_GROUP_NAME,
      headerName: t('headerVendorCompany'),
      field: 'vendorContactGroupName',
      filter: 'agTextColumnFilter',
      sortable: false,
    },
    vendorContactColumn: {
      colId: VendorQuoteColId.VENDOR_CONTACT_NAME,
      headerName: t('headerContact'),
      field: 'vendorContactName',
      filter: AutocompleteFilter,
      filterParams: {
        autocompleteProps: {
          getOptions: corporateUserService.getCorporateForeignUsersAutocomplete,
          placeholder: t('placeholderSelectUser'),
          requestPayload: {
            filters: {
              userType: CorporateUserType.VENDOR,
            },
          },
        },
        getAutocompleteValue: (option: ICorporateUserAutocomplete) => option.userId,
      },
    },
    jobColumn: {
      colId: VendorQuoteColId.JOB_NAME,
      headerName: t('headerJob'),
      field: 'jobName',
      filter: 'agTextColumnFilter',
    },
    projectColumn: {
      colId: VendorQuoteColId.CUSTOMER_PROJECT_NAME,
      headerName: t('headerProject'),
      field: 'customerProjectName',
      filter: 'agTextColumnFilter',
    },
    vendorProjectColumn: {
      colId: VendorQuoteColId.VENDOR_PROJECT_NAME,
      headerName: t('headerVendorProject'),
      field: 'vendorProjectName',
      filter: 'agTextColumnFilter',
    },
    totalPriceColumn: {
      colId: VendorQuoteColId.TOTAL_PRICE,
      headerName: isOrder ? t('headerOrderValue') : t('headerReleaseValue'),
      field: 'totalPrice',
      filter: 'agNumberColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.GREATER_THAN_OR_EQUAL,
      },
      valueFormatter: ({ data }) => PricingHelper.formatPricing({ amount: data.totalPrice }),
      cellStyle: { textAlign: 'right' },
    },
    userColumn: {
      colId: VendorQuoteColId.CUSTOMER_CORPORATE_USER_NAME,
      headerName: t('headerUser'),
      field: 'customerCorporateUserName',
      filter: 'agTextColumnFilter',
    },
    locationColumn: {
      colId: VendorQuoteColId.LOCATION_NAME,
      headerName: t('headerLocation'),
      field: 'locationName',
      filter: 'agTextColumnFilter',
    },
    updatedAtColumn: {
      colId: VendorQuoteColId.UPDATED_AT,
      headerName: t('headerUpdated'),
      field: 'updatedAt',
      valueFormatter: ({ value }) => formatDate(value),
      filter: 'agDateColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.GREATER_THAN,
      },
      sort: SortDirection.DESC,
    },
    createdAtColumn: {
      colId: VendorQuoteColId.CREATED_AT,
      headerName: t('headerCreated'),
      field: 'createdAt',
      valueFormatter: ({ value }) => formatDate(value),
      filter: 'agDateColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.GREATER_THAN,
      },
    },
    archivedColumn: {
      colId: VendorQuoteColId.ARCHIVED,
      headerName: t(`header${entityType}State`),
      field: 'archived',
      cellRenderer: QuoteStateCell,
      filter: 'agSetColumnFilter',
      filterParams: {
        suppressSelectAll: true,
        values: [QuoteState.ACTIVE, QuoteState.ARCHIVED],
      },
      sortable: false,
    },
    // Quote specific columns
    manufacturersColumn: {
      colId: VendorQuoteColId.MANUFACTURERS,
      headerName: t('headerManufacturers'),
      field: 'manufacturers',
      autoHeight: true,
      sortable: false,
      valueGetter: ({ data }) =>
        data.manufacturers?.map((manufacturer: { name: string }) => manufacturer.name),
      cellRenderer: ({ value }) => <TextLine text={value.join('<br/>')} maxRows={3} textEllipsis />,
    },
    vendorQuoteColumn: {
      colId: VendorQuoteColId.VENDOR_QUOTE_NUMBER,
      headerName: t('headerVendorQuoteNumber'),
      field: 'vendorQuoteNumber',
      filter: 'agTextColumnFilter',
      cellRenderer: (props: CustomCellRendererProps<IVendorQuote>) => (
        <Stack>
          <CellRendererForAgGrid {...props} />
          <TextLine
            text={t('labelRevisionWithValue', {
              value: props.data?.revisionNumber || DEFAULT_PLACEHOLDER,
            })}
            isSubText
          />
        </Stack>
      ),
    },
    vendorNameColumn: {
      colId: VendorQuoteColId.VENDOR_NAME,
      headerName: t('headerVendorName'),
      field: 'vendorName',
      filter: 'agTextColumnFilter',
      cellRenderer: VendorNameCell,
      sortable: false,
    },
    filenetStatusColumn: {
      colId: VendorQuoteColId.FILENET_STATUS,
      headerName: t('headerSentToFileNet'),
      field: 'filenetStatus',
      cellRenderer: FilenetStatusCell,
      filter: 'agSetColumnFilter',
      filterParams: {
        // EMPTY = never attempted (filenet_status IS NULL on the BE) — most VQs.
        values: [FilenetStatus.SENT, FilenetStatus.FAILED, FilenetStatus.SENDING, EMPTY],
        valueFormatter: ({ value }) =>
          EMPTY === value ? t('labelNotSentToFileNet') : FilenetStatusLabel[value] ?? '',
      },
      sortable: false,
    },
    validUntilColumn: {
      colId: VendorQuoteColId.VALID_UNTIL,
      headerName: t('headerValidUntil'),
      field: 'validUntil',
      valueFormatter: ({ value }) => value && formatDate(value),
      filter: 'agDateColumnFilter',
      filterParams: {
        includeBlanksInGreaterThan: true,
      },
    },
    tradeAgreementStatusColumn: {
      colId: VendorQuoteColId.TRADE_AGREEMENT_STATUS,
      headerName: t('headerTradeAgreementStatus'),
      field: 'tradeAgreementStatus',
      filter: 'agSetColumnFilter',
      filterParams: {
        values: Object.values(TradeAgreementStatus),
        defaultToNothingSelected: true,
      },
      cellRenderer: TradeAgreementStatusCell,
    },
    tradeAgreementOwnerColumn: {
      colId: VendorQuoteColId.TRADE_AGREEMENT_OWNER_NAME,
      headerName: t('headerTradeAgreementOwner'),
      field: 'tradeAgreementOwnerName',
      filter: 'agTextColumnFilter',
      cellRenderer: TradeAgreementOwnerCell,
    },

    // Order specific columns
    customerNumberColumn: {
      colId: VendorQuoteColId.EXTERNAL_CUSTOMER_NUMBER,
      headerName: t('headerCustomerNumber'),
      field: 'externalCustomerNumber',
      filter: 'agTextColumnFilter',
    },
    purchaseOrderColumn: {
      colId: VendorQuoteColId.PURCHASE_ORDER_NUMBER,
      headerName: t('headerPurchaseOrderNumber'),
      field: 'purchaseOrderNumber',
      filter: 'agTextColumnFilter',
    },
    externalOrderColumn: {
      colId: VendorQuoteColId.EXTERNAL_ORDER_NUMBER,
      headerName: t('headerExternalOrderNumber'),
      field: 'externalOrderNumber',
      filter: 'agTextColumnFilter',
      cellRenderer: LinkCell,
      cellRendererParams: {
        getPath: (data: IBaseQuote) => data.externalOrderUrl,
        target: LinkTarget.BLANK,
      },
    },
    externalPendingOrderColumn: {
      colId: VendorQuoteColId.EXTERNAL_PENDING_ORDER_NUMBER,
      headerName: t('headerExternalPendingOrderNumber'),
      field: 'externalPendingOrderNumber',
      filter: 'agTextColumnFilter',
      cellRenderer: LinkCell,
      cellRendererParams: {
        getPath: (data: IBaseQuote) => data.externalPendingOrderUrl,
        target: LinkTarget.BLANK,
      },
    },
  };
};
